import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc,
  updateDoc,
  query, 
  where, 
  orderBy,
  serverTimestamp,
  writeBatch,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { db } from './firebase';
import { ProcurementBatch, ProcurementBatchItem, Order, Bundle, Product } from '../types';
import { fetchProductById } from './productService';
import { fetchBundleById } from './bundleService';

/**
 * Get all procurement batches
 */
export const fetchProcurementBatches = async (): Promise<ProcurementBatch[]> => {
  try {
    const batchesCollection = collection(db, 'procurementBatches');
    const batchesQuery = query(batchesCollection, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(batchesQuery);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        batchNumber: data.batchNumber,
        orderIds: data.orderIds || [],
        items: data.items || [],
        priorityOrdersIncluded: data.priorityOrdersIncluded || false,
        totalCost: data.totalCost || 0,
        status: data.status || 'draft',
        pdfUrl: data.pdfUrl,
        publicUrl: data.publicUrl,
        publicToken: data.publicToken,
        checkedItems: data.checkedItems || [],
        actualQuantities: data.actualQuantities || {},
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt || new Date()),
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : (data.updatedAt || new Date()),
        createdBy: data.createdBy || '',
      } as ProcurementBatch;
    });
  } catch (error) {
    console.error('Error fetching procurement batches:', error);
    throw new Error('Failed to fetch procurement batches');
  }
};

/**
 * Get a single procurement batch by ID
 */
export const fetchProcurementBatchById = async (batchId: string): Promise<ProcurementBatch | null> => {
  try {
    const batchDoc = doc(db, 'procurementBatches', batchId);
    const snapshot = await getDoc(batchDoc);
    
    if (!snapshot.exists()) {
      return null;
    }
    
    const data = snapshot.data();
    return {
      id: snapshot.id,
      batchNumber: data.batchNumber,
      orderIds: data.orderIds || [],
      items: data.items || [],
      priorityOrdersIncluded: data.priorityOrdersIncluded || false,
      totalCost: data.totalCost || 0,
      status: data.status || 'draft',
      pdfUrl: data.pdfUrl,
      publicUrl: data.publicUrl,
      publicToken: data.publicToken,
      checkedItems: data.checkedItems || [],
      actualQuantities: data.actualQuantities || {},
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt || new Date()),
      updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : (data.updatedAt || new Date()),
      createdBy: data.createdBy || '',
    } as ProcurementBatch;
  } catch (error) {
    console.error('Error fetching procurement batch:', error);
    throw new Error('Failed to fetch procurement batch');
  }
};

/**
 * Get orders eligible for procurement (paid, unpacked, not in procurement)
 */
export const getEligibleOrdersForProcurement = async (): Promise<Order[]> => {
  try {
    const ordersCollection = collection(db, 'orders');
    const ordersQuery = query(
      ordersCollection,
      where('status', '==', 'paid'),
      orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(ordersQuery);
    const orders = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt || new Date()),
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : (data.updatedAt || new Date()),
      } as Order;
    });
    
    // Filter for unpacked orders (packed !== true or packed field doesn't exist)
    // and not already in procurement
    return orders.filter(order => {
      const isUnpacked = order.packed !== true; // This handles both false and undefined
      const notInProcurement = !order.inProcurement;
      return isUnpacked && notInProcurement;
    });
  } catch (error) {
    console.error('Error fetching eligible orders:', error);
    throw new Error('Failed to fetch eligible orders for procurement');
  }
};

/**
 * Calculate aggregated shopping list from selected orders
 */
export const calculateShoppingList = async (orders: Order[]): Promise<ProcurementBatchItem[]> => {
  try {
    const itemMap = new Map<string, ProcurementBatchItem>();
    const bundleCache = new Map<string, Bundle | null>();
    const productCache = new Map<string, Product | null>();

    const getProduct = async (productId: string): Promise<Product | null> => {
      if (!productId) return null;
      if (!productCache.has(productId)) {
        try {
          const product = await fetchProductById(productId);
          productCache.set(productId, product);
        } catch (error) {
          console.error('Failed to fetch product details for procurement:', productId, error);
          productCache.set(productId, null);
        }
      }
      return productCache.get(productId) ?? null;
    };

    const upsertProcurementItem = async ({
      productId,
      fallbackName,
      fallbackImage,
      orderQuantityDelta,
      requiredQuantityDelta,
      fallbackListingQuantity,
      fallbackUnitCost,
    }: {
      productId: string;
      fallbackName: string;
      fallbackImage?: string;
      orderQuantityDelta: number;
      requiredQuantityDelta: number;
      fallbackListingQuantity?: number;
      fallbackUnitCost?: number;
    }) => {
      if (!productId || orderQuantityDelta <= 0 || requiredQuantityDelta <= 0) {
        return;
      }

      const product = await getProduct(productId);
      const unitCost = product?.price ?? fallbackUnitCost ?? 0;
      const listingQuantity = fallbackListingQuantity ?? product?.listingQuantity ?? 1;
      const image = product?.image || product?.images?.[0] || fallbackImage || '';
      const name = product?.name || fallbackName || productId;

      const existingItem = itemMap.get(productId);
      if (existingItem) {
        existingItem.orderQuantity += orderQuantityDelta;
        existingItem.requiredQuantity += requiredQuantityDelta;
        existingItem.unitCost = unitCost;
        existingItem.lineTotal = existingItem.orderQuantity * unitCost;
        existingItem.productName = name;
        if (!existingItem.image && image) {
          existingItem.image = image;
        }
        if (!existingItem.brand && product?.brand) {
          existingItem.brand = product.brand;
        }
        if (!existingItem.category && product?.category) {
          existingItem.category = product.category;
        }
        if (product?.netWeight != null) {
          existingItem.netWeight = product.netWeight;
        }
        if (product?.weightUnit) {
          existingItem.weightUnit = product.weightUnit;
        }
        if (listingQuantity && listingQuantity !== existingItem.listingQuantity) {
          existingItem.listingQuantity = listingQuantity;
        }
        if (!existingItem.image && image) {
          existingItem.image = image;
        }
      } else {
        const newItem: ProcurementBatchItem = {
          productId,
          productName: name,
          image,
          requiredQuantity: requiredQuantityDelta,
          orderQuantity: orderQuantityDelta,
          listingQuantity,
          unitCost,
          lineTotal: orderQuantityDelta * unitCost,
        };

        if (product?.brand) {
          newItem.brand = product.brand;
        }
        if (product?.category) {
          newItem.category = product.category;
        }
        if (product?.netWeight != null) {
          newItem.netWeight = product.netWeight;
        }
        if (product?.weightUnit) {
          newItem.weightUnit = product.weightUnit;
        }

        itemMap.set(productId, newItem);
      }
    };

    const processStandardItem = async (item: Order['items'][number]) => {
      const productId = item.id;
      const orderQuantityDelta = item.quantity;
      const requiredQuantityDelta = (item.listingQuantity || 1) * item.quantity;

      await upsertProcurementItem({
        productId,
        fallbackName: item.name,
        fallbackImage: item.image,
        orderQuantityDelta,
        requiredQuantityDelta,
        fallbackListingQuantity: item.listingQuantity || 1,
        fallbackUnitCost: item.price,
      });
    };

    for (const order of orders) {
      for (const item of order.items) {
        if (item.id?.startsWith('bundle:')) {
          const [, bundleIdRaw] = item.id.split(':');
          const bundleId = bundleIdRaw?.trim();

          if (!bundleId) {
            await processStandardItem(item);
            continue;
          }

          if (!bundleCache.has(bundleId)) {
            try {
              const bundle = await fetchBundleById(bundleId);
              bundleCache.set(bundleId, bundle);
            } catch (error) {
              console.error('Failed to fetch bundle details for procurement:', bundleId, error);
              bundleCache.set(bundleId, null);
            }
          }

          const bundle = bundleCache.get(bundleId);
          const bundleQuantity = (item.listingQuantity || 1) * item.quantity;

          if (!bundle || !Array.isArray(bundle.items) || bundle.items.length === 0) {
            await processStandardItem(item);
            continue;
          }

          for (const bundleItem of bundle.items) {
            const productId = bundleItem.productId;
            const perBundleCount = (bundleItem.quantity || 1);
            const listingQuantity = bundleItem.listingQuantity || 1;
            const orderQuantityDelta = bundleQuantity * perBundleCount;
            const requiredQuantityDelta = orderQuantityDelta * listingQuantity;

            await upsertProcurementItem({
              productId,
              fallbackName: bundleItem.productName || bundleItem.productId,
              fallbackImage: bundleItem.image,
              orderQuantityDelta,
              requiredQuantityDelta,
              fallbackListingQuantity: listingQuantity,
            });
          }
        } else {
          await processStandardItem(item);
        }
      }
    }

    return Array.from(itemMap.values()).sort((a, b) => a.productName.localeCompare(b.productName));
  } catch (error) {
    console.error('Error calculating shopping list:', error);
    throw new Error('Failed to calculate shopping list');
  }
};

/**
 * Generate batch number
 */
export const generateBatchNumber = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = (now.getMonth() + 1).toString().padStart(2, '0');
  const day = now.getDate().toString().padStart(2, '0');
  const time = now.getHours().toString().padStart(2, '0') + 
               now.getMinutes().toString().padStart(2, '0');
  
  return `PB-${year}${month}${day}-${time}`;
};

/**
 * Create a new procurement batch
 */
export const createProcurementBatch = async (
  orderIds: string[],
  items: ProcurementBatchItem[],
  priorityOrdersIncluded: boolean,
  createdBy: string
): Promise<string> => {
  try {
    if (orderIds.length === 0) {
      throw new Error('At least one order is required to create a procurement batch.');
    }

    const orderEntries = orderIds.map((orderId) => ({
      orderId,
      ref: doc(db, 'orders', orderId)
    }));

    const orderSnapshots = await Promise.all(orderEntries.map(({ ref }) => getDoc(ref)));
    const missingOrderIds = orderEntries
      .filter((_, index) => !orderSnapshots[index].exists())
      .map(({ orderId }) => orderId);

    if (missingOrderIds.length > 0) {
      throw new Error(
        `The following orders could not be found: ${missingOrderIds.join(', ')}. Please refresh the orders list and try again.`
      );
    }

    const validOrderIds = orderEntries.map(({ orderId }) => orderId);

    const batchesCollection = collection(db, 'procurementBatches');
    const batchNumber = generateBatchNumber();
    const totalCost = items.reduce((sum, item) => sum + item.lineTotal, 0);
    
    const batch = {
      batchNumber,
      orderIds: validOrderIds,
      items,
      priorityOrdersIncluded,
      totalCost,
      status: 'draft',
      checkedItems: [], // Initialize empty checked items array
      actualQuantities: {}, // Initialize empty actual quantities object
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy,
    };
    
    const docRef = await addDoc(batchesCollection, batch);
    
    // Update orders to mark them as in procurement
    const batchUpdate = writeBatch(db);
    orderEntries.forEach(({ ref }) => {
      batchUpdate.update(ref, {
        inProcurement: true,
        procurementBatchId: docRef.id,
        updatedAt: serverTimestamp(),
      });
    });

    await batchUpdate.commit();
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating procurement batch:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to create procurement batch');
  }
};

/**
 * Update procurement batch status
 */
export const updateProcurementBatchStatus = async (
  batchId: string, 
  status: ProcurementBatch['status'],
  pdfUrl?: string
): Promise<void> => {
  try {
    const batchRef = doc(db, 'procurementBatches', batchId);
    const updateData: any = {
      status,
      updatedAt: serverTimestamp(),
    };
    
    if (pdfUrl) {
      updateData.pdfUrl = pdfUrl;
    }
    
    await updateDoc(batchRef, updateData);
  } catch (error) {
    console.error('Error updating procurement batch status:', error);
    throw new Error('Failed to update procurement batch status');
  }
};

/**
 * Remove orders from procurement (when batch is cancelled)
 */
export const removeOrdersFromProcurement = async (orderIds: string[]): Promise<void> => {
  try {
    const batch = writeBatch(db);
    
    for (const orderId of orderIds) {
      const orderRef = doc(db, 'orders', orderId);
      batch.update(orderRef, {
        inProcurement: false,
        procurementBatchId: null,
        updatedAt: serverTimestamp(),
      });
    }
    
    await batch.commit();
  } catch (error) {
    console.error('Error removing orders from procurement:', error);
    throw new Error('Failed to remove orders from procurement');
  }
};

/**
 * Generate a unique public URL for a procurement batch
 */
export const generatePublicProcurementLink = async (batchId: string): Promise<string> => {
  try {
    // Generate a cryptographically secure random token
    const publicToken = crypto.randomUUID();
    const baseUrl = window.location.origin;
    const publicUrl = `${baseUrl}/procurement-batch/${batchId}/${publicToken}`;
    
    // Update the batch with the public URL and token
    const batchRef = doc(db, 'procurementBatches', batchId);
    await updateDoc(batchRef, {
      publicUrl,
      publicToken,
      updatedAt: serverTimestamp(),
    });
    
    return publicUrl;
  } catch (error) {
    console.error('Error generating public procurement link:', error);
    throw new Error('Failed to generate public procurement link');
  }
};

/**
 * Fetch procurement batch by public token (for public access)
 */
export const fetchProcurementBatchByToken = async (batchId: string, token: string): Promise<ProcurementBatch | null> => {
  try {
    const batchDoc = doc(db, 'procurementBatches', batchId);
    const snapshot = await getDoc(batchDoc);
    
    if (!snapshot.exists()) {
      return null;
    }
    
    const data = snapshot.data();
    
    // Verify the token matches
    if (data.publicToken !== token) {
      throw new Error('Invalid access token');
    }
    
    return {
      id: snapshot.id,
      batchNumber: data.batchNumber,
      orderIds: data.orderIds || [],
      items: data.items || [],
      priorityOrdersIncluded: data.priorityOrdersIncluded || false,
      totalCost: data.totalCost || 0,
      status: data.status || 'draft',
      pdfUrl: data.pdfUrl,
      publicUrl: data.publicUrl,
      publicToken: data.publicToken,
      checkedItems: data.checkedItems || [],
      actualQuantities: data.actualQuantities || {},
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt || new Date()),
      updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : (data.updatedAt || new Date()),
      createdBy: data.createdBy || '',
    } as ProcurementBatch;
  } catch (error) {
    console.error('Error fetching procurement batch by token:', error);
    throw new Error('Failed to fetch procurement batch');
  }
};

/**
 * Toggle an item's checked status in a procurement batch
 */
export const toggleItemChecked = async (batchId: string, productId: string, isChecked: boolean): Promise<void> => {
  try {
    const batchRef = doc(db, 'procurementBatches', batchId);
    
    if (isChecked) {
      // Add to checked items
      await updateDoc(batchRef, {
        checkedItems: arrayUnion(productId),
        updatedAt: serverTimestamp(),
      });
    } else {
      // Remove from checked items
      await updateDoc(batchRef, {
        checkedItems: arrayRemove(productId),
        updatedAt: serverTimestamp(),
      });
    }
  } catch (error) {
    console.error('Error toggling item checked status:', error);
    throw new Error('Failed to update item status');
  }
};

/**
 * Update multiple checked items at once
 */
export const updateCheckedItems = async (batchId: string, checkedItems: string[]): Promise<void> => {
  try {
    const batchRef = doc(db, 'procurementBatches', batchId);
    await updateDoc(batchRef, {
      checkedItems,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating checked items:', error);
    throw new Error('Failed to update checked items');
  }
};

/**
 * Update actual quantity for a specific item in a procurement batch
 */
export const updateItemActualQuantity = async (batchId: string, productId: string, actualQuantity: number): Promise<void> => {
  try {
    const batchRef = doc(db, 'procurementBatches', batchId);
    
    // Use nested field update to update specific product in actualQuantities map
    await updateDoc(batchRef, {
      [`actualQuantities.${productId}`]: actualQuantity,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating item actual quantity:', error);
    throw new Error('Failed to update item actual quantity');
  }
};

/**
 * Merge multiple procurement batches into one
 */
export const mergeProcurementBatches = async (
  batchIds: string[], 
  createdBy: string
): Promise<string> => {
  try {
    if (batchIds.length < 2) {
      throw new Error('At least 2 batches are required for merging');
    }

    // Fetch all batches to merge
    const batches = await Promise.all(
      batchIds.map(id => fetchProcurementBatchById(id))
    );

    // Filter out null batches
    const validBatches = batches.filter(batch => batch !== null) as ProcurementBatch[];
    
    if (validBatches.length !== batchIds.length) {
      throw new Error('Some batches could not be found');
    }

    // Merge items - aggregate quantities for duplicate products
    const itemMap = new Map<string, ProcurementBatchItem>();
    
    for (const batch of validBatches) {
      for (const item of batch.items) {
        const existing = itemMap.get(item.productId);
        if (existing) {
          // Aggregate quantities
          existing.orderQuantity += item.orderQuantity;
          existing.requiredQuantity += item.requiredQuantity;
          existing.lineTotal = existing.orderQuantity * existing.unitCost;
        } else {
          // Create copy to avoid reference issues
          itemMap.set(item.productId, { ...item });
        }
      }
    }

    const mergedItems = Array.from(itemMap.values()).sort((a, b) => 
      a.productName.localeCompare(b.productName)
    );

    // Combine all order IDs
    const allOrderIds = Array.from(new Set(
      validBatches.flatMap(batch => batch.orderIds)
    ));

    // Calculate total cost
    const totalCost = mergedItems.reduce((sum, item) => sum + item.lineTotal, 0);

    // Check if any batches have priority orders
    const priorityOrdersIncluded = validBatches.some(batch => batch.priorityOrdersIncluded);

    // Create merged batch
    const batchNumber = generateBatchNumber();
    const mergedBatch = {
      batchNumber,
      orderIds: allOrderIds,
      items: mergedItems,
      priorityOrdersIncluded,
      totalCost,
      status: 'draft' as const, // Reset to draft status
      checkedItems: [], // Reset checkboxes
      actualQuantities: {}, // Reset quantities
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy,
    };

    // Use batch operation for atomicity
    const batch = writeBatch(db);

    // Create new merged batch
    const newBatchRef = doc(collection(db, 'procurementBatches'));
    batch.set(newBatchRef, mergedBatch);

    // Update all orders to reference new batch
    for (const orderId of allOrderIds) {
      const orderRef = doc(db, 'orders', orderId);
      batch.update(orderRef, {
        procurementBatchId: newBatchRef.id,
        updatedAt: serverTimestamp(),
      });
    }

    // Delete old batches
    for (const batchId of batchIds) {
      const oldBatchRef = doc(db, 'procurementBatches', batchId);
      batch.delete(oldBatchRef);
    }

    await batch.commit();

    return newBatchRef.id;
  } catch (error) {
    console.error('Error merging procurement batches:', error);
    throw new Error('Failed to merge procurement batches');
  }
};

/**
 * Delete a single procurement batch and clean up order references
 */
export const deleteProcurementBatch = async (batchId: string): Promise<void> => {
  try {
    // Fetch batch to get order IDs
    const procurementBatch = await fetchProcurementBatchById(batchId);
    
    if (!procurementBatch) {
      throw new Error('Procurement batch not found');
    }

    // Use batch operation for atomicity
    const batch = writeBatch(db);

    // Update orders to remove procurement flags
    for (const orderId of procurementBatch.orderIds) {
      const orderRef = doc(db, 'orders', orderId);
      batch.update(orderRef, {
        inProcurement: false,
        procurementBatchId: null,
        updatedAt: serverTimestamp(),
      });
    }

    // Delete batch
    const batchRef = doc(db, 'procurementBatches', batchId);
    batch.delete(batchRef);

    await batch.commit();
  } catch (error) {
    console.error('Error deleting procurement batch:', error);
    throw new Error('Failed to delete procurement batch');
  }
};

/**
 * Delete multiple procurement batches
 */
export const deleteProcurementBatches = async (batchIds: string[]): Promise<void> => {
  try {
    if (batchIds.length === 0) {
      throw new Error('No batches provided for deletion');
    }

    // Delete each batch (this handles the order cleanup automatically)
    await Promise.all(batchIds.map(batchId => deleteProcurementBatch(batchId)));
  } catch (error) {
    console.error('Error deleting procurement batches:', error);
    throw new Error('Failed to delete procurement batches');
  }
};
